import { EnumSportType } from '@/types';

enum PlayerType {
    B2B,
    B2C,
    B2B2C,
}

interface ICustomerSettings {
    isEFootballEnabled: boolean;
    isSimulationEnabled: boolean;
    isCricketEnabled: boolean;
    playerType: PlayerType;
    isInEFootballLeagueBlackList: boolean;
}

interface IOutright {
    isWalkinOnly: boolean;
    tournament?: {
        sport?: {
            sportType: EnumSportType;
        } | null;
    } | null;
}

interface IEvent {
    isSimulation: boolean;
    tournament?: {
        sport?: {
            sportType: EnumSportType;
        } | null;
        id?: number;
    } | null;
}

interface IEventInfo {
    eventCount: number;
    simulationEventCount: number;
    filterEFootballEventCount: number;
}

type SportsInfo = {
    sport: EnumSportType;
    eventCount: number;
    liveEventCount: number;
    mixParlayEventCount: number;
    liveMixParlayEventCount: number;
    outrightTournamentCount: number;
    liveOutrightTournamentCount: number;
    simulationEventCount: number;
    liveSimulationEventCount: number;
    mixParlaySimulationEventCount: number;
    liveMixParlaySimulationEventCount: number;
    filterEFootballEventCount: number;
    liveFilterEFootballEventCount: number;
    mixParlayFilterEFootballEventCount: number;
    liveMixParlayFilterEFootballEventCount: number;
}

class BusinessLogicHelper {
    public static isOutrightAllowed(customerSettings: ICustomerSettings, outright: IOutright) {
        const sportType = outright.tournament?.sport?.sportType;

        if (!sportType) {
            throw new Error('Sport type is undefined');
        }

        if (!this.isAllowedSport(customerSettings, sportType)) {
            return false;
        }

        if (customerSettings.playerType !== PlayerType.B2C) {
            return !outright.isWalkinOnly;
        }

        return true;
    }

    public static isEventAllowed(customerSettings: ICustomerSettings, event: IEvent, unstableMarginLeagueIds: number[]) {
        const sportType = event.tournament?.sport?.sportType;

        if (!sportType) {
            throw new Error('Sport type is undefined');
        }

        if (event.isSimulation && !this.isAllowSimulationEvents(customerSettings, sportType)) {
            return false;
        }

        if (!this.isAllowedSport(customerSettings, sportType)) {
            return false;
        }

        const isUnstableMarginEvent = unstableMarginLeagueIds.includes(event?.tournament?.id ?? 0);
        if (isUnstableMarginEvent && !this.isAllowUnstableMarginEvents(customerSettings)) {
            return false;
        }

        return true;
    }

    public static filterEventCount(
        eventInfo: IEventInfo,
        customerSettings: ICustomerSettings,
        sportType: EnumSportType,
    ) {
        const eventCount = eventInfo.eventCount;

        if (!this.isAllowedSport(customerSettings, sportType)) {
            return 0;
        }

        if (!this.isAllowSimulationEvents(customerSettings, sportType)) {
            return eventCount - eventInfo.simulationEventCount;
        }

        if (!this.isAllowUnstableMarginEvents(customerSettings)) {
            return eventCount - eventInfo.filterEFootballEventCount;
        }

        return eventCount;
    }

    public static filterSportInfo(sportInfo: SportsInfo, customerSettings: ICustomerSettings): SportsInfo {
        if (!this.isAllowedSport(customerSettings, sportInfo.sport)) {
            return {
                ...sportInfo,
                eventCount: 0,
                liveEventCount: 0,
                simulationEventCount: 0,
                mixParlayEventCount: 0,
                liveMixParlayEventCount: 0,
                mixParlaySimulationEventCount: 0,
                liveMixParlaySimulationEventCount: 0,
                outrightTournamentCount: 0,
                liveOutrightTournamentCount: 0,
                liveSimulationEventCount: 0,
                filterEFootballEventCount: 0,
                liveFilterEFootballEventCount: 0,
                mixParlayFilterEFootballEventCount: 0,
                liveMixParlayFilterEFootballEventCount: 0,
            };
        }

        if (!this.isAllowSimulationEvents(customerSettings, sportInfo.sport)) {
            return {
                ...sportInfo,
                eventCount: sportInfo.eventCount - sportInfo.simulationEventCount,
                liveEventCount: sportInfo.liveEventCount - sportInfo.liveSimulationEventCount,
                mixParlayEventCount: sportInfo.mixParlayEventCount - sportInfo.mixParlaySimulationEventCount,
                liveMixParlayEventCount: sportInfo.liveMixParlayEventCount - sportInfo.liveMixParlaySimulationEventCount,
                simulationEventCount: 0,
                liveSimulationEventCount: 0,
                mixParlaySimulationEventCount: 0,
                liveMixParlaySimulationEventCount: 0,
                filterEFootballEventCount: 0,
                liveFilterEFootballEventCount: 0,
                mixParlayFilterEFootballEventCount: 0,
                liveMixParlayFilterEFootballEventCount: 0,
            };
        }

        if (this.isAllowSimulationEvents(customerSettings, sportInfo.sport) && !this.isAllowUnstableMarginEvents(customerSettings)) {
            return {
                ...sportInfo,
                eventCount: sportInfo.eventCount - sportInfo.filterEFootballEventCount,
                liveEventCount: sportInfo.liveEventCount - sportInfo.liveFilterEFootballEventCount,
                mixParlayEventCount: sportInfo.mixParlayEventCount - sportInfo.mixParlayFilterEFootballEventCount,
                liveMixParlayEventCount: sportInfo.liveMixParlayEventCount - sportInfo.liveMixParlayFilterEFootballEventCount,
            };
        }

        return sportInfo;
    }

    public static isAllowedSport(customerSettings: ICustomerSettings, sportType: EnumSportType) {
        if (sportType === EnumSportType.Cricket) {
            return customerSettings.isCricketEnabled;
        }

        return true;
    }

    private static isAllowUnstableMarginEvents(customerSettings: ICustomerSettings) {
        return !customerSettings.isInEFootballLeagueBlackList;
    }

    private static isAllowSimulationEvents(customerSettings: ICustomerSettings, sportType: EnumSportType) {
        if (sportType === EnumSportType.Soccer) {
            return customerSettings.isEFootballEnabled && customerSettings.isSimulationEnabled;
        }

        return customerSettings.isSimulationEnabled;
    }
}

export default BusinessLogicHelper;
